/**
 * Contact info editor (BR-LSI6-02 — required phone/name before activation), moved from
 * ListingDetailPage.tsx's ContactInfoSection.
 *
 *   variant 'card'  the Contact & agent section: a card with a read-only view and an Edit button.
 *   variant 'bare'  hosted in an edit sheet or a guide step: the form only, no card, no
 *                   read-only view; Save and Cancel call `onDone` so the host can collapse/close.
 *   embedded        (bare only) hosted in the listing guide: no Save/Cancel of its own; the step's
 *                   "Save and continue" calls `handleRef.save()`. Saving never blocks moving on:
 *                   blank fields are allowed here, and only publishing requires a name and phone.
 */

import { useState, useEffect, useImperativeHandle, useRef, type Ref } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getManagerProfile } from '../../../lib/account.api';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { updateListing, type Listing } from '../../../lib/leasing.api';
import type { GuideStepHandle } from '../listing-guide/guide-steps';
import { formatPhoneDisplay } from './shared';
import { getIntakeEmailState } from './ShareCopyButtons';

/** The manager's own account name/phone — the pre-fill suggestion source (and the setup state's "Defaulted" signal). */
export const MANAGER_PROFILE_QUERY = {
  queryKey: ['managerProfile'] as const,
  queryFn: getManagerProfile,
};

interface ContactInfoEditorProps {
  listing: Listing;
  intakeAddress: string | null;
  contactPhone: string | null;
  contactName: string | null;
  activationError: string | null;
  onSaved: () => void;
  variant?: 'card' | 'bare';
  /** Bare variant: called after a successful save or on Cancel. */
  onDone?: () => void;
  /** Bare variant in a guide step: hide the form's own Save/Cancel (the step saves via `handleRef`). */
  embedded?: boolean;
  handleRef?: Ref<GuideStepHandle>;
  /** Fires when the unsaved-edits flag flips. */
  onDirtyChange?: (dirty: boolean) => void;
  onSavingChange?: (saving: boolean) => void;
}

export function ContactInfoEditor({
  listing,
  intakeAddress,
  contactPhone,
  contactName,
  activationError,
  onSaved,
  variant = 'card',
  onDone,
  embedded = false,
  handleRef,
  onDirtyChange,
  onSavingChange,
}: ContactInfoEditorProps) {
  const isBare = variant === 'bare';
  // Pre-fill suggestion source only — the manager's own account phone/name. Existing
  // query in this app (see SettingsProfilePage / RejectionNoticeModal); no new endpoint.
  const { data: managerProfile } = useQuery(MANAGER_PROFILE_QUERY);

  const [phone, setPhone] = useState(contactPhone ?? '');
  const [name, setName] = useState(contactName ?? '');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Card: read-only-then-Edit, starting in edit mode when nothing is saved yet. Bare: the host
  // already chose to edit (it opened the row/sheet), so the form shows straight away.
  const isClosed = listing.status === 'closed';
  const [editing, setEditing] = useState(isBare || (!isClosed && !contactPhone && !contactName));

  // Activating can fail with CONTACT_INFO_REQUIRED; surface that by switching to edit mode
  // instead of leaving the error hidden behind the read-only view.
  useEffect(() => {
    if (activationError) setEditing(true);
  }, [activationError]);

  // Default each field to the manager's account value only while the listing has no
  // saved contact channel of its own and the landlord hasn't started editing it. This
  // is an editable suggestion, not a persisted value — Save (and therefore this
  // listing's own ListingContactChannel rows) is still required before Activate will
  // accept it.
  useEffect(() => {
    if (contactPhone || phoneTouched || !managerProfile?.phone) return;
    setPhone(managerProfile.phone);
  }, [managerProfile, contactPhone, phoneTouched]);

  useEffect(() => {
    if (contactName || nameTouched || !managerProfile) return;
    const fullName = [managerProfile.firstName, managerProfile.lastName]
      .filter((part): part is string => !!part)
      .join(' ');
    if (fullName) setName(fullName);
  }, [managerProfile, contactName, nameTouched]);

  const persist = async (): Promise<boolean> => {
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const payload = { contactPhone: phone.trim(), contactName: name.trim() };
      await updateListing(listing.id, payload);
      onSaved();
      return true;
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save contact info');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!(await persist())) return;
    if (isBare) {
      onDone?.();
      return;
    }
    setEditing(false);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };

  // Unsaved = the landlord typed something that differs from what is saved. The manager-profile
  // suggestion alone is not an edit (it is saved by "Save and continue", but never prompts on leave).
  const differsFromSaved = phone.trim() !== (contactPhone ?? '') || name.trim() !== (contactName ?? '');
  const dirty = (phoneTouched || nameTouched) && differsFromSaved;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    onSavingChange?.(isSaving);
  }, [isSaving, onSavingChange]);

  useImperativeHandle(handleRef, () => ({
    // Nothing to write when the fields already match what is saved (this includes both blank).
    save: async () => (differsFromSaved ? persist() : true),
    isDirty: () => dirtyRef.current,
  }));

  const handleCancel = () => {
    setPhone(contactPhone ?? '');
    setName(contactName ?? '');
    setPhoneTouched(false);
    setNameTouched(false);
    setSaveError(null);
    if (isBare) {
      onDone?.();
      return;
    }
    setEditing(false);
  };

  const { isDraft, isPending } = getIntakeEmailState(listing, intakeAddress);
  // The suggestion is showing (nothing saved yet, nothing typed): say where it came from.
  const prefilled = !contactPhone && !contactName && !phoneTouched && !nameTouched && !!phone && !!name;

  // Save persists blank fields pre-activation (BR-LSI6-01) — only Activate enforces
  // these as required, so the invalid/required a11y semantics below are keyed off
  // whichever error is currently showing rather than a static `aria-required`.
  const currentErrorId = saveError
    ? 'contact-info-save-error'
    : activationError
      ? 'contact-info-activation-error'
      : null;
  const hasError = currentErrorId !== null;

  const body = (
    <>
      {!embedded && (
        <>
          <p className={cn('text-sm text-muted-foreground mb-4', !isBare && 'mt-1')}>
            Shown on the public listing. Prospects can call this number.
          </p>

          <div className="mb-4">
            <span className="block text-sm font-medium mb-1">Intake email</span>
            {isDraft ? (
              <p className="text-sm text-muted-foreground" data-testid="contact-info-intake-email-empty">
                Your intake email address is created when you publish.
              </p>
            ) : isPending ? (
              <p className="text-sm text-muted-foreground" data-testid="contact-info-intake-email-empty">
                Address pending…
              </p>
            ) : (
              <p className="text-sm text-foreground break-all" data-testid="contact-info-intake-email-value">
                {intakeAddress}
              </p>
            )}
          </div>
        </>
      )}

      {editing ? (
        <div className={cn(embedded ? 'grid gap-x-4 gap-y-3 sm:grid-cols-2' : 'space-y-3 max-w-sm')}>
          <div>
            <label htmlFor="contact-info-name" className="block text-sm font-medium mb-1">
              Contact name <span className="text-destructive" aria-hidden="true">*</span>
            </label>
            <Input
              id="contact-info-name"
              data-testid="contact-info-name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameTouched(true);
              }}
              disabled={isSaving}
              aria-invalid={hasError ? true : undefined}
              aria-describedby={
                [hasError ? currentErrorId : null, 'contact-info-name-hint']
                  .filter((id): id is string => id !== null)
                  .join(' ')
              }
              className={cn('min-h-[44px]', hasError && 'border-destructive')}
            />
            <p id="contact-info-name-hint" className="text-xs text-muted-foreground mt-1">
              Required to publish
            </p>
          </div>

          <div>
            <label htmlFor="contact-info-phone" className="block text-sm font-medium mb-1">
              Contact phone <span className="text-destructive" aria-hidden="true">*</span>
            </label>
            <Input
              id="contact-info-phone"
              data-testid="contact-info-phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                setPhoneTouched(true);
              }}
              disabled={isSaving}
              aria-invalid={hasError ? true : undefined}
              aria-describedby={
                [hasError ? currentErrorId : null, 'contact-info-phone-hint']
                  .filter((id): id is string => id !== null)
                  .join(' ')
              }
              className={cn('min-h-[44px]', hasError && 'border-destructive')}
            />
            <p id="contact-info-phone-hint" className="text-xs text-muted-foreground mt-1">
              Required to publish
            </p>
          </div>
          {embedded && (
            <p className="text-xs text-muted-foreground sm:col-span-2" data-testid="contact-info-embedded-note">
              {prefilled ? 'Prefilled from your profile. Change it if prospects should reach someone else. ' : ''}
              Your listing email is created when you publish.
            </p>
          )}
          {!embedded && (
            <div className="flex items-center gap-3 pt-1">
              <Button
                type="button"
                size="sm"
                data-testid="contact-info-save"
                onClick={() => void handleSave()}
                disabled={isSaving}
                className="min-h-[44px]"
              >
                {isSaving ? 'Saving…' : 'Save Contact Info'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="contact-info-cancel"
                onClick={handleCancel}
                disabled={isSaving}
                className="min-h-[44px]"
              >
                Cancel
              </Button>
            </div>
          )}

          {saveError && (
            <div
              id="contact-info-save-error"
              data-testid="contact-info-save-error"
              className={cn(
                'p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm',
                embedded && 'sm:col-span-2'
              )}
              role="alert"
            >
              {saveError}
            </div>
          )}

          {activationError && (
            <div
              id="contact-info-activation-error"
              data-testid="contact-info-activation-error"
              className={cn(
                'p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm',
                embedded && 'sm:col-span-2'
              )}
              role="alert"
            >
              {activationError}
            </div>
          )}
        </div>
      ) : (
        <dl className="grid grid-cols-2 gap-4 text-sm max-w-sm" data-testid="contact-info-readonly">
          <div>
            <dt className="text-muted-foreground mb-0.5">Contact phone</dt>
            <dd className="font-medium" data-testid="contact-info-phone-value">
              {contactPhone ? formatPhoneDisplay(contactPhone) : <span className="text-muted-foreground italic">Not set</span>}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground mb-0.5">Contact name</dt>
            <dd className="font-medium" data-testid="contact-info-name-value">
              {contactName || <span className="text-muted-foreground italic">Not set</span>}
            </dd>
          </div>
        </dl>
      )}
      {saveSuccess && (
        <span
          role="status"
          aria-live="polite"
          data-testid="contact-info-save-success"
          className="text-sm text-success font-medium"
        >
          Saved.
        </span>
      )}
    </>
  );

  if (isBare) {
    return <div data-testid="contact-info-section">{body}</div>;
  }

  return (
    <section data-testid="contact-info-section" className="mb-6" aria-labelledby="contact-info-title">
      <div className="rounded-lg border border-border bg-card p-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h2 id="contact-info-title" className="text-base font-semibold">
            Contact info
          </h2>
          {!isClosed && !editing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="contact-info-edit-button"
              onClick={() => setEditing(true)}
              className="min-h-[44px]"
            >
              Edit
            </Button>
          )}
        </div>
        {body}
      </div>
    </section>
  );
}
